/**
 * AlarmCenter — Sprint 4.2A.
 *
 * Widget flutuante posicionado no topo-direito do container do mapa.
 * Exibe contadores por categoria de alarme. Clicar numa categoria
 * toggla `filters.status` (clicar de novo limpa).
 *
 * Categorias (alinhadas com paleta de marker do hotfix #2):
 *   🔴 offline    — !online
 *   🟠 relay off  — online && relay='off'
 *   🟡 weak       — online && weak_signal
 *   🟢 online     — online && relay='on' && !weak_signal
 *
 * Filtros aplicados (precisam estar suportados em matchesFilters):
 *   offline    → filters.status='offline'
 *   relay_off  → filters.status='relay_off'   (ADICIONADO em FleetFilters)
 *   weak       → filters.status='weak'
 *   online     → filters.status='online'
 */
import { useMemo, useState } from "react";

// M1.1C — recolhido por padrão em telas mobile (< 768px).
const _isMobile = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 767px)").matches;

// M1.3B — ordem do painel Frota: Offline · Online OK · Relé OFF · RSSI fraco.
const CATEGORIES = [
  { id: "offline",   label: "Offline",   filter: "offline",   color: "#FF5063", bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.4)" },
  { id: "online",    label: "Online OK", filter: "online",    color: "#22c55e", bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.4)" },
  { id: "relay_off", label: "Relé OFF",  filter: "relay_off", color: "#FF8A3C", bg: "rgba(249,115,22,0.12)",  border: "rgba(249,115,22,0.4)" },
  { id: "weak",      label: "RSSI fraco", filter: "weak",     color: "#FFB224", bg: "rgba(234,179,8,0.12)",   border: "rgba(234,179,8,0.4)" },
];

function computeCounts(devices) {
  let offline = 0, relayOff = 0, weak = 0, online = 0;
  for (const d of devices || []) {
    if (!d.online) {
      offline += 1;
    } else if (d.relay === "off") {
      relayOff += 1;
    } else if (d.weak_signal) {
      weak += 1;
    } else {
      online += 1;
    }
  }
  return { offline, relay_off: relayOff, weak, online };
}

export default function AlarmCenter({
  devices, filters, setFilters, onOpenList,
  collapsed: collapsedProp, onToggle,
}) {
  const counts = useMemo(() => computeCounts(devices), [devices]);
  const active = filters.status || "";
  // M1.1C — recolhível; inicia recolhido no mobile, aberto no desktop.
  // P0-5 — quando `collapsedProp` é passado (mobile coordenado), o componente
  // vira CONTROLADO; sem ele (desktop), mantém o estado interno atual.
  const [internalCollapsed, setInternalCollapsed] = useState(() => _isMobile());
  const isControlled = collapsedProp !== undefined;
  const collapsed = isControlled ? collapsedProp : internalCollapsed;
  const toggleCollapsed = () =>
    isControlled ? onToggle?.() : setInternalCollapsed((v) => !v);

  const handleClick = (filter) => {
    setFilters({ ...filters, status: active === filter ? "" : filter });
  };

  // M1.3 — painel "Frota": total = nº de devices (não a soma das categorias).
  const total = devices?.length ?? 0;

  return (
    <div className="ipwd-alarm-center">
      <button
        type="button"
        className="ipwd-alarm-header"
        onClick={toggleCollapsed}
        title={collapsed ? "Expandir painel Frota" : "Recolher painel Frota"}
      >
        <span>{collapsed ? "📡" : "▾"} Frota</span>
        <span className="ipwd-alarm-total">{total}</span>
      </button>
      {collapsed ? null : (
      <div className="ipwd-alarm-list">
        {CATEGORIES.map((cat) => {
          const isActive = active === cat.filter;
          const count = counts[cat.id] ?? 0;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleClick(cat.filter)}
              className={`ipwd-alarm-pill ${isActive ? "is-active" : ""} ${count === 0 ? "is-empty" : ""}`}
              style={{
                "--alarm-color": cat.color,
                "--alarm-bg": cat.bg,
                "--alarm-border": cat.border,
              }}
              title={
                isActive
                  ? `Limpar filtro ${cat.label}`
                  : `Filtrar por ${cat.label}`
              }
            >
              <span className="ipwd-alarm-dot" />
              <span className="ipwd-alarm-label">{cat.label}</span>
              <span className="ipwd-alarm-count">{count}</span>
            </button>
          );
        })}
      </div>
      )}
      {!collapsed && onOpenList && (
        <button
          type="button"
          onClick={onOpenList}
          className="ipwd-frota-openlist"
          title="Abrir lista de devices / filtros"
        >
          ↗ Abrir lista
        </button>
      )}
      {!collapsed && active && (
        <button
          type="button"
          onClick={() => setFilters({ ...filters, status: "" })}
          className="ipwd-alarm-clear"
        >
          ✕ limpar filtro
        </button>
      )}
    </div>
  );
}
